import { logger } from './logger';

/**
 * Credential persistence layer for the embed running in a cross-site context.
 *
 * The alchemist runs embedded as a third-party iframe: `embedstore.ingresse.com`
 * inside the producer's site (e.g. camarotefanzone.com.br). In that context the
 * browser drops `.ingresse.com` cookies — they are third-party. Verified on
 * Safari/iOS 26 (iPhone) and on Chrome desktop with tracking protection enabled:
 * right after `document.cookie = ...` the read comes back EMPTY, while storage
 * partitioned by top-site writes and reads normally.
 *
 * The consequence was api-embedstore's ES0002 ("JWT token missing!"): login completed
 * and handed over the `authToken` in memory, `updateUser` wrote only to the cookie,
 * the cookie evaporated, and the "Proceed" step's `POST /shop/validate` went out with
 * no `Authorization` header — the backend rejects with 401 before ever looking at the
 * token contents.
 *
 * DELIBERATELY NARROW SCOPE: both reads and writes only happen once `cookiesAreUsable`
 * proves the browser is dropping cookies. Where the cookie works (ingresse.com, the
 * app, any same-site context) this module is inert: it neither reads nor writes.
 * Without that gate, a logout performed OUTSIDE the alchemist — which clears the
 * `.ingresse.com` cookie but cannot reach `embedstore.ingresse.com` storage — would
 * let the stale credential come back to life: a zombie session in flows that are
 * healthy today.
 *
 * WHY `sessionStorage` AND NOT `localStorage`: a credential in JavaScript-readable
 * storage is exposed to XSS, so the window is narrowed to the minimum that fixes the
 * bug — the current tab. Close the tab and the credential is gone; the whole purchase
 * flow, refresh included, happens in a single tab. Note this does NOT worsen the
 * current posture: the cookie used today is written via `document.cookie` and is
 * therefore already readable by JavaScript.
 *
 * The definitive fix for the embed is the backend issuing the credential as an
 * `HttpOnly; Secure; SameSite=None; Partitioned` (CHIPS) cookie instead of handing it
 * to JS — then it stops being reachable by script in any context. That requires
 * changes in auth/api and is out of scope for this correction.
 */

/** In-memory mirror: the only layer that survives storage being unavailable. */
const memory = new Map<string, string>();

/** Dedicated namespace, so we never collide with other keys on the same origin. */
const STORAGE_PREFIX = 'ing_cred:';

/** Throwaway cookie used only to find out whether the browser accepts cookies here. */
const PROBE_COOKIE = 'ing_1_probe';

/**
 * The probe has to reproduce the SAME write the real cookie does (`setCookieValue.ts`),
 * domain included: a host cookie may be accepted where a domain cookie is refused, and
 * the domain one is what the app depends on.
 */
const COOKIE_DOMAIN = '.ingresse.com';

/** Probe result, resolved once per page session. */
let cookiesUsable: boolean | null = null;

/**
 * `sessionStorage` can throw (private mode, storage blocked by policy) and does not
 * exist outside the browser. Every access goes through here so we degrade without
 * breaking the flow — memory still covers the current session. The failure is logged:
 * unavailable storage is exactly the kind of condition that, silenced, turns into an
 * unreproducible bug.
 */
const safeSessionStorage = <T>(operation: (storage: Storage) => T, context: string): T | null => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;

    return operation(window.sessionStorage);
  } catch (error) {
    logger.warn('credentialsStorage: sessionStorage unavailable, falling back to memory', {
      operation : context,
      error : (error as Error)?.name,
    });

    return null;
  }
};

/**
 * Finds out, by experiment, whether the browser persists cookies in this context.
 *
 * Writes a throwaway cookie — same shape as the real one, domain included — and tries
 * to read it back. In a same-site context it comes back (and is removed right after);
 * in a cross-site iframe with third-party cookies blocked the read comes back empty,
 * which is exactly the symptom that breaks login.
 *
 * It is an empirical check on purpose: detecting by user-agent or by
 * `window.self !== window.top` would be wrong in both directions — not every iframe is
 * cross-site, and the blocking also hits people who enabled tracking protection outside
 * an iframe. The result is memoized because the policy does not change during the page.
 */
export const cookiesAreUsable = (): boolean => {
  if (cookiesUsable !== null) return cookiesUsable;

  try {
    if (typeof document === 'undefined') {
      cookiesUsable = false;
      return cookiesUsable;
    }

    document.cookie = `${PROBE_COOKIE}=1; domain=${COOKIE_DOMAIN}; path=/`;
    cookiesUsable = document.cookie.includes(`${PROBE_COOKIE}=`);

    if (cookiesUsable) {
      document.cookie = `${PROBE_COOKIE}=; domain=${COOKIE_DOMAIN}; path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  } catch (error) {
    logger.warn('credentialsStorage: cookie probe failed, assuming a blocked context', {
      error : (error as Error)?.name,
    });
    cookiesUsable = false;
  }

  return cookiesUsable;
};

/**
 * Reads a credential from the layers that stand in for the cookie.
 *
 * Returns `undefined` when the cookie works in the current context: there the cookie is
 * the source of truth and the caller (`getCookieValue` / `getIngCookieValue`) has
 * already read it. See the scope note at the top of this file — that is what prevents
 * zombie sessions.
 *
 * @param cookieName FULL cookie name, environment prefix included (e.g. `ing_1_jwt`),
 *                   so every caller keeps computing the key exactly as it does today.
 */
export const readCredential = (cookieName: string): string | undefined => {
  if (cookiesAreUsable()) return undefined;

  const fromMemory = memory.get(cookieName);
  if (fromMemory) return fromMemory;

  const fromStorage = safeSessionStorage(
    (storage) => storage.getItem(`${STORAGE_PREFIX}${cookieName}`),
    'read',
  );
  if (!fromStorage) return undefined;

  // Rehydrate memory so a later storage failure cannot drop the session.
  memory.set(cookieName, fromStorage);

  return fromStorage;
};

/**
 * Writes a credential to memory and `sessionStorage`.
 *
 * Writes nothing when the cookie works: there the cookie already covers the flow, and
 * persisting a copy of the credential would only widen the surface exposed to XSS for
 * no benefit at all.
 *
 * An empty value removes instead of writing: `updateUser` calls with `authToken || ''`
 * when auth returns no token, and a persisted empty string would mask the absence of a
 * credential — the caller would read `''` as if it were a valid cached value.
 */
export const writeCredential = (cookieName: string, value: string): void => {
  if (!value) {
    removeCredential(cookieName);
    return;
  }

  if (cookiesAreUsable()) return;

  memory.set(cookieName, value);
  safeSessionStorage((storage) => storage.setItem(`${STORAGE_PREFIX}${cookieName}`, value), 'write');
};

/**
 * Removes a credential from both layers.
 *
 * Unconditional on purpose: cleanup has to reach leftovers written while the context
 * was different. Erring towards deleting too much is safe; deleting too little is what
 * would leave an orphan credential behind.
 */
export const removeCredential = (cookieName: string): void => {
  memory.delete(cookieName);
  safeSessionStorage((storage) => storage.removeItem(`${STORAGE_PREFIX}${cookieName}`), 'remove');
};

/**
 * Names of every known credential, merging memory and `sessionStorage`. Used by the
 * bulk cleanup, which needs to sweep what exists without relying on `Cookies.get()` —
 * that one comes back empty in a cross-site context.
 */
export const listCredentialNames = (): string[] => {
  const names = new Set<string>(memory.keys());

  safeSessionStorage((storage) => {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);

      if (key?.startsWith(STORAGE_PREFIX)) {
        names.add(key.slice(STORAGE_PREFIX.length));
      }
    }

    return null;
  }, 'list');

  return [...names];
};
